<script lang="ts">
	import { ElectrumClient, ElectrumTransport as Transport } from '@electrum-cash/network';
	import { onMount } from 'svelte';
	import {
		getAllBalances,
		Vault,
		getHodlAddresses,
		getLichoWillAddresses,
		getLichoMecenasAddresses,
		getUnspentAddresses,
		getUnspentV1Addresses,
		
	} from '@fbch/lib';

	import Loading from '$lib/Loading.svelte';
	import { height } from '$lib/store.js';

	let heightValue: number;

	height.subscribe((value: any) => {
		heightValue = value;
	});

	let electrum;
	let protocols = [];

	onMount(async () => {
		// Initialize an electrum client.
		electrum = new ElectrumClient(
			'FBCH/webapp',
			'1.4.1',
			'bch.imaginary.cash',
			Transport.WSS.Port,
			Transport.WSS.Scheme
		);
		await electrum.connect();

		let unspentAddresses = await getUnspentAddresses();
		let unspentV1Addresses = await getUnspentV1Addresses();
		let hodlAddresses = await getHodlAddresses();
		let willAddresses = await getLichoWillAddresses();
		let mecenasAddresses = await getLichoMecenasAddresses();

		protocols = [
			{
				name: 'Hodl',
				src: 'https://github.com/mainnet-pat/hodl_ec_plugin/',
				docs: 'https://github.com/mainnet-pat/hodl_ec_plugin/blob/master/README.md',
				app: 'https://github.com/mainnet-pat/hodl_ec_plugin/releases',
				addresses: hodlAddresses,
				description: 'Timelock BCH with an Electron Cash Plugin',
				image: '/hodl.png',
				tlv: NaN
			},
			{
				name: 'Future Bitcoin Cash',
				addresses: Vault.getAllSeries(heightValue ? heightValue: 881000 ),
				docs: 'https://futurebitcoin.cash/contracts',
				app: 'https://futurebitcoin.cash/',
				src: 'https://github.com/2qx/future-bitcoin-cash/',
				description: 'Tokenized timelocked BCH',
				image: '/FBCH.png',
				tlv: NaN
			},
			{
				name: 'BadgerCoin',
				addresses: ['bitcoincash:pvgcl3xk6nwqlngkk09e7g67x5vxs57jv6v2q4qm4ct5yv4d3ppfgl3tq982v'],
				docs: 'https://badgers.cash/FAQ',
				app: 'https://badgers.cash',
				src: 'https://github.com/SayoshiNakamario/BadgersStake',
				description: 'A proof-of-ownership memecoin paying token rewards for time-locking BCH.',
				image: '/badgercoin.png',
				tlv: NaN
			},
			{
				name: 'Unspent Phi',
				docs: 'https://unspent.app/documentation',
				app: 'https://unspent.app/',
				src: 'https://github.com/2qx/unspent/tree/main/packages/phi',
				description: 'Generic composable contracts with timelocking available.',
				addresses: unspentV1Addresses,
				image: '/unspent1.png',
				tlv: NaN
			},
			{
				name: 'Unspent Cash',
				docs: 'https://unspent.cash/help',
				app: 'https://unspent.cash/',
				src: 'https://github.com/2qx/unspent/tree/main/packages/chi',
				description: 'Get one percent of a fund monthly in perpetuity.',
				addresses: unspentAddresses,
				image: '/unspent2.png',
				tlv: NaN
			},
			{
				name: 'Emerald Dao',
				addresses: ['bitcoincash:pr43rx2gwdq6j2dpmrpxldftu7swfn7xvqga6vzmp3'],
				docs: 'https://emerald-dao.cash/',
				app: 'https://emerald-dao.vercel.app/',
				src: 'https://gitlab.com/0353F40E/emerald-dao/-/tree/main',
				description: '[CLOSED FUND] A fixed-term deposit savings vault using NFTs with crowdfunded rewards.',
				image: '/emerald.jpg',
				tlv: NaN
			},
			{
				name: 'Wrapped Bitcoin Cash',
				addresses: ['bitcoincash:p0ujgnc9jnyurzv99678fgac3fdrq8x3py9rlrg6dlnz96qxrdl02efwc0sf9'],
				docs: 'https://bitcoincashresearch.org/t/wbch-bch-wrapped-as-cash-token/1196',
				app: 'https://wrapped.cash/',
				src: 'https://gitlab.com/dagurval/wrapped-cash',
				description: 'Bitcoin Cash Tokenized 1:1 as fungible CashTokens.',
				image: '/WBCH.png',
				tlv: NaN
			},
			{
				name: 'Mecenas',
				addresses: mecenasAddresses,
				docs: 'https://github.com/KarolTrzeszczkowski/Mecenas-recurring-payment-EC-plugin',
				app: 'https://github.com/KarolTrzeszczkowski/Mecenas-recurring-payment-EC-plugin',
				src: 'https://github.com/KarolTrzeszczkowski/Mecenas-recurring-payment-EC-plugin',
				description: 'A recurring payment plugin for a noncustodial patronate',
				image: '/mecenas.svg',
				tlv: NaN
			},
			{
				name: 'Last Will',
				addresses: willAddresses,
				docs: 'https://github.com/KarolTrzeszczkowski/Electron-Cash-Last-Will-Plugin',
				app: 'https://github.com/KarolTrzeszczkowski/Electron-Cash-Last-Will-Plugin',
				src: 'https://github.com/KarolTrzeszczkowski/Electron-Cash-Last-Will-Plugin',
				description: 'A dead man\'s switch with a six month trigger.',
				image: '/licho.png',
				tlv: NaN
			}
		];

		const getTlvs = async () => {
			Promise.all(
				protocols.map(async (v, i) => {
					protocols[i].tlv =
						(await getAllBalances(electrum, v.addresses)).reduce((a, b) => a + b, 0) / 1e8;
					protocols = protocols.sort((a, b) => b.tlv - a.tlv);
				})
			).then(() => {
				console.log('here');
				// protocols = protocols.sort((a, b) => b.tlv -a.tlv);
			});
		};
		await getTlvs();
	});
</script>

<svelte:head>
	<title>Future BCH</title>
	<meta name="description" content="Future Bitcoin Cash" />
	<link rel="icon" type="image/svg" href="/FBCH.svg" />
</svelte:head>

<section>
	<h3>The Bitcoin Cash Savings Club</h3>
	<p>Decentralized Financial Instruments based on wrapped and time locked Bitcoin Cash (BCH)</p>
	{#if protocols.length > 0}
		<table>
			<thead>
				<tr class="header">
					<td></td>
					<td></td>
					<td></td>
					<td style="font-weight:600">TLV</td>
				</tr>
			</thead>
			<tbody>
				{#each protocols as p, i (p.name)}
					<tr>
						<td> <div style="font-weight:900">#{i}</div></td>
						<td> <img src={p.image} width="40" /></td>
						<td>
							<a style="color:#333333; font-weight:600;" href={p.app} target="_blank">{p.name}</a
							><br />
							<p style="font-size:small">
								{p.description}
								{#if p.docs}
									<a style="color:#333333;" target="_blank" href={p.docs}>[docs]</a>
								{/if}

								{#if p.src}
									<a style="color:#333333;" target="_blank" href={p.src}>[code]</a>
								{/if}
							</p>
						</td>

						<td style="text-align:right; font-weight:600">
							{#if p.tlv}
								{p.tlv.toLocaleString(undefined, {
									maximumSignificantDigits: 3,
									minimumSignificantDigits: 1
								})} ʙᴄʜ
							{:else}
								<Loading />
							{/if}
							<p style="font-size:small"></p>
						</td>
					</tr>
				{/each}
			</tbody>
		</table>
	{:else}
		<p>Getting total amount locked by protocol</p>
		<Loading />
	{/if}
</section>

<style>
	table {
		border-collapse: collapse;
	}
	td {
		font-size: large;
		border: 0px;
	}
	tbody tr:nth-child(even) {
		background: radial-gradient(#c455c428, #ffdaff00);
	}
	tbody tr:nth-child(odd) {
		background: radial-gradient(#ffffff55, #ffdaff00);
	}
</style>
